import React, { useState } from 'react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { useToolResultContent } from './ToolResultDataContext';

import { useTranslation } from 'react-i18next';

interface TerminalCommandBlockProps {
  content: ToolUseBlock;
  isStreaming: boolean;
}

interface ShellResult {
  stdout?: string;
  stderr?: string;
  exitCode?: number;
  executionTimeMs?: number;
  sessionName?: string;
}

const MAX_COLLAPSED_LINES = 20;

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`h-3 w-3 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="2.5"
      stroke="currentColor"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.25 4.5l7.5 7.5-7.5 7.5"
      />
    </svg>
  );
}

function TerminalIcon() {
  return (
    <svg
      className="h-3.5 w-3.5"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.75"
      stroke="currentColor"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z"
      />
    </svg>
  );
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) {
    return '';
  }
  if (ms < 1000) {
    return `${ms}ms`;
  }
  if (ms < 60_000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
}

function OutputLine({ line }: { line: string }) {
  const lower = line.toLowerCase();
  const isError =
    /\b(error|fatal|fail|exception|traceback|panic)\b/.test(lower) ||
    line.startsWith('E ') ||
    line.startsWith('ERRO');
  const isWarning =
    /\b(warn|warning|deprecated)\b/.test(lower) ||
    line.startsWith('W ') ||
    line.startsWith('WARN');
  const isSuccess =
    /\b(success|done|complete|passed)\b/.test(lower) ||
    line.includes('✓') ||
    line.includes('✔');

  const cls = isError
    ? 'text-red-400'
    : isWarning
      ? 'text-yellow-300'
      : isSuccess
        ? 'text-emerald-400'
        : 'text-gray-200';

  return <div className={`${cls} min-h-[1em]`}>{line || '\u00A0'}</div>;
}

const OutputBlock: React.FC<{ text: string; isStderr?: boolean }> = ({
  text,
  isStderr = false,
}) => {
  const { t } = useTranslation('pleri/chat');
  const [expanded, setExpanded] = useState(false);
  const lines = text.split('\n');
  const truncated = lines.length > MAX_COLLAPSED_LINES && !expanded;
  const visible = truncated ? lines.slice(0, MAX_COLLAPSED_LINES) : lines;

  return (
    <div>
      {isStderr && (
        <div className="mb-1 text-xs font-semibold tracking-wider text-red-400/70 uppercase">
          {t('stderr')}
        </div>
      )}
      <div className="font-mono text-xs leading-relaxed">
        {visible.map((line, i) => (
          <OutputLine key={i} line={line} />
        ))}
      </div>
      {truncated && (
        <button
          className="mt-1.5 text-xs text-gray-500 transition-colors hover:text-gray-300"
          onClick={() => setExpanded(true)}
        >
          {t('moreLinesClickToExpand', { count: lines.length - MAX_COLLAPSED_LINES })}
        </button>
      )}
    </div>
  );
};

function parseResult(raw: string): ShellResult {
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed as ShellResult;
    }
    // JSON but not an object (e.g. a plain string) — treat as stdout
    return { stdout: String(parsed), exitCode: 0 };
  } catch {
    // Not JSON — treat as plain stdout text
    return { stdout: raw, exitCode: 0 };
  }
}

/**
 * Renders a shellRun tool call as an inline terminal block.
 *
 * Running state is derived from `pendingToolUses` (authoritative) rather than
 * `isStreaming` so it's accurate during the tool-execution gap between when
 * the tool call completes and when the result arrives.
 *
 * Result is looked up by toolUseId so multiple shellRun calls in the
 * same message each show their own output independently.
 */
export const TerminalCommandBlock: React.FC<TerminalCommandBlockProps> = ({
  content,
}) => {
  const { t } = useTranslation('pleri/chat');
  const [cmdExpanded, setCmdExpanded] = useState(false);

  // pendingToolUses is the authoritative "still executing" signal
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const isPending = useChatStore((s) =>
    activeConversationId
      ? (
          s.conversations[activeConversationId]?.pendingToolUses ?? new Set()
        ).has(content.id)
      : false,
  );

  // Look up result by toolUseId (supports multiple calls per message)
  const resultJson = useToolResultContent(content.id);

  const command =
    typeof content.input?.command === 'string' ? content.input.command : null;
  const session =
    typeof content.input?.session === 'string' ? content.input.session : null;
  const parallel = content.input?.parallel === true;

  if (!command) {
    return null;
  }

  const result = resultJson ? parseResult(resultJson) : undefined;
  const isRunning = isPending || (!result && !resultJson);
  const failed =
    result != null && result.exitCode != null && result.exitCode !== 0;
  const stdout = result?.stdout?.trim() ?? '';
  const stderr = result?.stderr?.trim() ?? '';
  const hasOutput = !!(stdout || stderr);

  const commandLines = command.split('\n');
  const isLongCommand = command.length > 120 || commandLines.length > 1;
  const displayCommand =
    !cmdExpanded && isLongCommand
      ? commandLines[0].slice(0, 120) + ' …'
      : command;

  const duration =
    result?.executionTimeMs != null
      ? formatDuration(result.executionTimeMs)
      : '';

  return (
    <div className="my-1 overflow-hidden rounded-xl border border-gray-800 bg-gray-950 text-xs">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 border-b border-gray-800 bg-gray-900/80 px-3 py-1.5">
        <div className="flex gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-gray-700" />
          <div className="h-2.5 w-2.5 rounded-full bg-gray-700" />
          <div className="h-2.5 w-2.5 rounded-full bg-gray-700" />
        </div>

        <div className="flex flex-1 items-center gap-1.5 text-gray-500">
          <TerminalIcon />
          <span>
            {session && session !== 'main'
              ? t('sessionName', { session })
              : t('shell')}
          </span>
          {parallel && (
            <span className="rounded bg-gray-700/60 px-1 py-0.5 text-xs text-gray-400">
              {t('parallel')}
            </span>
          )}
        </div>

        {duration && (
          <span className="text-xs text-gray-600">{duration}</span>
        )}

        {failed && (
          <span className="rounded bg-red-900/50 px-1.5 py-0.5 font-mono text-xs text-red-400">
            {t('exit', { exitCode: result?.exitCode })}
          </span>
        )}

        {/* Status indicator */}
        {isRunning ? (
          <svg
            className="h-2.5 w-2.5 animate-spin text-yellow-400"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : failed ? (
          <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
        ) : (
          <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
        )}
      </div>
      {/* ── Command ───────────────────────────────────────────────────── */}
      <div className="px-3 pt-2.5 pb-2">
        <div
          className={isLongCommand ? 'cursor-pointer' : ''}
          onClick={() => isLongCommand && setCmdExpanded((v) => !v)}
        >
          <div className="flex items-start gap-2">
            <span className="font-mono text-emerald-400 select-none">$</span>
            <pre className="flex-1 font-mono break-all whitespace-pre-wrap text-gray-100">
              {displayCommand}
            </pre>
            {isLongCommand && (
              <span className="mt-0.5 flex-shrink-0 text-gray-600">
                <ChevronIcon expanded={cmdExpanded} />
              </span>
            )}
          </div>
        </div>
      </div>
      {/* ── Output ─────────────────────────────────────────────────────── */}
      {hasOutput && (
        <div className="border-t border-gray-800/60 px-3 py-2.5">
          <div className="max-h-48 overflow-y-auto">
            {stdout && <OutputBlock text={stdout} />}
            {stderr && (
              <div className={stdout ? 'mt-3' : ''}>
                <OutputBlock text={stderr} isStderr />
              </div>
            )}
          </div>
        </div>
      )}
      {/* ── Running cursor ─────────────────────────────────────────────── */}
      {isRunning && !hasOutput && (
        <div className="border-t border-gray-800/60 px-3 py-2">
          <span className="inline-block h-3 w-1.5 animate-pulse bg-gray-400" />
        </div>
      )}
    </div>
  );
};
